/* eslint-disable no-console */
'use client';
import { useEffect, useState } from 'react';
import { RudderAnalytics } from '@rudderstack/analytics-js';
import { RudderTyperAnalytics } from '../types/index';
import { getVisitorId } from '../helpers/cookies';
import { getStageMode } from '../helpers/getStageMode';
import { ProxiedAnalytics } from '../proxies/proxiedAnalytics';
const writeKey = process.env.NEXT_PUBLIC_RUDDER_WRITE_KEY;
const dataPlaneUrl = process.env.NEXT_PUBLIC_RUDDER_DATA_PLANE_URL;
// Skip validation in test environments, CI, or when explicitly disabled
const skipValidation = Boolean(process.env.CI) ||
    Boolean(process.env.SKIP_ENV_VALIDATION) ||
    process.env.npm_lifecycle_event === 'lint' ||
    getStageMode() === 'test';
if (!skipValidation && (!writeKey || !dataPlaneUrl)) {
    throw new Error('AnalyticsProvider: writeKey or dataPlaneUrl is not provided');
}
let initPromise = null;
export const useAnalytics = () => {
    const [rudder, setRudder] = useState();
    useEffect(() => {
        if (!rudder) {
            const initialize = async () => {
                // If initialization is already in progress, wait for it
                if (initPromise) {
                    const instance = await initPromise;
                    if (instance) {
                        setRudder(instance);
                    }
                    return;
                }
                initPromise = new Promise((resolve) => {
                    const instance = new RudderAnalytics();
                    if (!writeKey || !dataPlaneUrl) {
                        console.warn('[Rudder] Analytics disabled - missing configuration');
                        return;
                    }
                    instance.load(writeKey, dataPlaneUrl, {
                        sessions: {
                            autoTrack: true,
                            timeout: 30 * 60 * 1000, //resets user analytics data after 30 minutes of inactivity
                            cutOff: {
                                enabled: true,
                            },
                        },
                        onLoaded: () => {
                            console.log('[Rudder] loaded');
                            const visitorId = getVisitorId();
                            if (visitorId) {
                                instance.setAnonymousId(visitorId);
                            }
                        },
                    });
                    instance.ready(() => {
                        console.log('[Rudder] ready');
                        resolve(instance);
                    });
                });
                const instance = await initPromise;
                if (instance) {
                    setRudder(instance);
                }
            };
            initialize().catch((error) => {
                console.error('Error initializing RudderStack analytics:', error);
            });
        }
    }, [rudder]);
    const analytics = rudder ?? (typeof window === 'undefined' ? undefined : window.rudderanalytics);
    if (analytics) {
        RudderTyperAnalytics.setRudderTyperOptions({ analytics });
    }
    return {
        analytics: analytics ? ProxiedAnalytics : undefined,
        resetAnalytics: () => {
            analytics?.reset?.(true);
        },
    };
};
